import { GCameraShakeController } from "../../../animationController";
import { GAudioEngine } from "../../../core/audioEngine";
import { GUserInputDesc } from "../../../core/input";
import { GStack, GetVec3, Vector3 } from "../../../core/types";
import {
    GTime,
    MathGetVec3Length,
    MathIntersectionRayAABB,
    MathIntersectionRayPlane,
    MathLerp,
    MathMapToRangeClamped,
} from "../../../core/utils";
import { GSimpleShapesRenderer } from "../../../helpers/shapeRender";
import { TransformFromNDCToWorld } from "../../../helpers/transform";
import { SceneBase } from "../../scene";
import { EPaintingState, PayneSceneDestrPainting } from "./destrPaintingMain";

export class BulletParticle {
    readonly CurPos = GetVec3(0, 0, 0);
    readonly PrevPos = GetVec3(0, 0, 0);
    readonly CurDir = GetVec3(0, 0, 0);
    readonly SpawnPos = GetVec3(0, 0, 0);
    bAlive = false;
}

export enum EBulletTimeState {
    Disabled,
    Enabled,
    ReturnToDisabled,
}

export class GBulletSystem {
    static GInstance: GBulletSystem | null = null;

    SpawnFrequencyDelta = 0.1 * 0.25 * 1.5;
    BulletSpawnCounter = 0;
    MaxNumBullets = 100;
    //LastSpawnTime = 0;
    TimeSinceLastSpawn = 0.0;
    TimeSinceLastSpawn2 = 0.0;
    CurSpawnHeadIndex = 0;
    BulletSpawnPos = GetVec3(0.0, 2.55, -9.75);
    kBulletMaxSpeed = MathLerp(120, 180, Math.random());
    BulletSpeed = this.kBulletMaxSpeed;

    kBulletTimeStartThresSec = 2.5;
    kBulletTimeReturnThresSec = 0.2;
    kBulletTimeEndThresSec = this.kBulletTimeReturnThresSec + 1.1;
    BulletTimeTriggerParam = 0.0; //Rises when long fire, lowers when stop
    BulletTimeState = EBulletTimeState.Disabled; //0 disabled 1 enabled 2 return to disabled

    NumTimesBulletTimeEnabled = 0;

    BulletTimeStartParam = 0.0;
    BulletTimeEndParam = 2.0;
    TimeSinceBulletTimeEnabled = 0.0;
    TimeSinceBulletTimeDisabled = 10.0;
    /* kBulletTimeStartThres = 1.2;
    kBulletTimeEndThres = 0.5; */

    BulletsArr: BulletParticle[];

    bIntersectionThisFrame = false;
    bSpawnParticlesThisFrame = false;
    bLampIntersectionThisFrame = false;
    bSceneObjectIntersectionThisFrame = false;

    NumTimesHeartAreaShot = 0;
    bHeartIntersectionThisFrame = false;

    IntersectionPos = GetVec3(0, 0, 0);
    HeartIntersectionPos = GetVec3(0, 0, 0);

    constructor() {
        this.BulletsArr = Array.from({ length: this.MaxNumBullets }, () => new BulletParticle());
    }

    SpawnNewBullet(curBulSpawnPos: Vector3, dirOffset: number) {
        const newBullet = this.BulletsArr[this.CurSpawnHeadIndex];
        this.CurSpawnHeadIndex += 1;
        if (this.CurSpawnHeadIndex > this.MaxNumBullets - 1) {
            this.CurSpawnHeadIndex = 0;
        }
        //this.LastSpawnTime = GTime.Cur;
        this.TimeSinceLastSpawn = 0.0;

        newBullet.bAlive = true;

        //newBullet.CurPos.Set(this.BulletSpawnPos);
        newBullet.SpawnPos.Set(curBulSpawnPos);
        newBullet.CurPos.Set(curBulSpawnPos);
        newBullet.CurPos.z += 0.5 * (-0.5 + Math.random());

        const ws = GStack.TempVec3_2;
        TransformFromNDCToWorld(
            GUserInputDesc.InputPosCurNDC,
            -SceneBase.UniformBuffer.Params.CameraPosition.z - 0.01,
            ws,
        );

        //apply jitter to aim
        {
            const jitterScale = 0.4;
            ws.x += jitterScale * (-0.5 + Math.random());
            ws.y += jitterScale * (-0.5 + Math.random());
        }

        ws.x += dirOffset;

        newBullet.CurDir.Set(ws);
        newBullet.CurDir.Negate(newBullet.CurPos);
        newBullet.CurDir.Normalize();

        //GCameraShakeController.ShakeCameraFast(0.1 * 1.0);
    }

    GetBulletSpawnPos(outPos: Vector3, bRight = true) {
        outPos.Set(GBulletSystem.GInstance!.BulletSpawnPos);
        outPos.x += GUserInputDesc.InputPosCurNDC.x * 0.1;
        let sign = 1.0;
        if (bRight) {
        } else {
            sign = -1.0;
        }

        outPos.x += 0.3 * sign;
    }

    OnUpdate(gl: WebGL2RenderingContext, scene: PayneSceneDestrPainting) {
        const audioContext = GAudioEngine.Instance!.Context;

        this.bIntersectionThisFrame = false;

        const dt = GTime.Delta * GTime.DeltaTimeScale;

        if (this.BulletTimeState == EBulletTimeState.Disabled) {
            this.TimeSinceBulletTimeDisabled += GTime.Delta;
        }
        if (this.BulletTimeState == EBulletTimeState.ReturnToDisabled) {
            this.TimeSinceBulletTimeDisabled += GTime.Delta;

            this.BulletTimeTriggerParam += GTime.Delta;

            if (this.BulletTimeTriggerParam > this.kBulletTimeEndThresSec) {
                this.TimeSinceBulletTimeDisabled = 0.0;
                this.BulletTimeState = EBulletTimeState.Disabled;
                this.BulletTimeTriggerParam = 0.0;
            }
        }

        if (this.BulletTimeState == EBulletTimeState.Enabled) {
            this.TimeSinceBulletTimeEnabled += GTime.Delta;

            if (!GUserInputDesc.bPointerInputPressedCurFrame) {
                this.BulletTimeTriggerParam += GTime.Delta;

                if (this.BulletTimeTriggerParam > this.kBulletTimeReturnThresSec) {
                    this.TimeSinceBulletTimeDisabled = 0.0;
                    this.BulletTimeState = EBulletTimeState.ReturnToDisabled;

                    if (audioContext) {
                        scene.SoundBulletTimeEnd.Play(audioContext);
                    }
                }
            }
        }

        if (GUserInputDesc.bPointerInputPressedCurFrame && scene.PaintState != EPaintingState.AfterDeath) {
            if (!GUserInputDesc.bPointerInputPressedPrevFrame) {
                //scene.BulletTimeStartParam = 0.0;
                this.BulletSpawnCounter = 0;
            }
            if (scene.PaintState < EPaintingState.HeartCanShoot) {
                if (
                    this.BulletTimeState == EBulletTimeState.Disabled &&
                    scene.PaintState != EPaintingState.InitNoHeal
                ) {
                    this.BulletTimeTriggerParam = this.BulletTimeTriggerParam + GTime.Delta;
                    if (this.BulletTimeTriggerParam > this.kBulletTimeStartThresSec) {
                        this.BulletTimeState = EBulletTimeState.Enabled;
                        this.NumTimesBulletTimeEnabled += 1;
                        this.BulletTimeTriggerParam = 0.0;
                        this.TimeSinceBulletTimeEnabled = 0.0;

                        if (this.NumTimesBulletTimeEnabled == 3) {
                            scene.SoundEnableBackFilter();
                        }

                        if (audioContext) {
                            scene.SoundBulletTimeStart.Play(audioContext);
                        }
                    }
                }
            }
        }

        let spawnDelta = this.SpawnFrequencyDelta;
        const numBulletsContSpawn = 7;
        if (this.BulletSpawnCounter == numBulletsContSpawn - 1) {
            spawnDelta *= 1.5 + Math.random() * 0.25;
        } else {
        }

        const flickerFreqDelta = spawnDelta; /* * 2.0 */
        if (GUserInputDesc.bPointerInputPressedCurFrame && this.TimeSinceLastSpawn2 > flickerFreqDelta) {
            this.TimeSinceLastSpawn2 = 0.0;
        }

        this.bSpawnParticlesThisFrame =
            GUserInputDesc.bPointerInputPressedCurFrame &&
            /* !GUserInputDesc.bPointerInputPressedPrevFrame && */
            this.TimeSinceLastSpawn > spawnDelta &&
            scene.PaintState != EPaintingState.AfterDeath;

        if (this.bSpawnParticlesThisFrame) {
            this.BulletSpawnCounter = (this.BulletSpawnCounter + 1) % numBulletsContSpawn;
        }

        const buf = SceneBase.UniformBuffer.Params;
        if (this.bSpawnParticlesThisFrame) {
            buf.PointLightPos2.Set(GBulletSystem.GInstance!.BulletSpawnPos);
            buf.PointLightPos2.z += 0.0;
            buf.PointLightPos2.z = -10.0;
            buf.PointLightPos2.x += 0.5 + GUserInputDesc.InputPosCurNDC.x * 3.0;
        } else if (this.TimeSinceLastSpawn2 > spawnDelta * 0.5) {
            buf.PointLightPos2.Set3(100, 100, 100);
        }

        if (this.bSpawnParticlesThisFrame) {
            const dirOffset = 0.1;
            const curBulSpawnPos = GStack.TempVec3_4;
            this.GetBulletSpawnPos(curBulSpawnPos, true);
            this.SpawnNewBullet(curBulSpawnPos, 0.1 + dirOffset);

            this.GetBulletSpawnPos(curBulSpawnPos, false);
            this.SpawnNewBullet(curBulSpawnPos, -0.35 - dirOffset);
        }

        //Intersections

        this.bSceneObjectIntersectionThisFrame = false;
        this.bHeartIntersectionThisFrame = false;
        this.bLampIntersectionThisFrame = false;

        for (const bulletRef of this.BulletsArr) {
            if (bulletRef.bAlive) {
                const vel = GStack.TempVec3;
                vel.Set(bulletRef.CurDir);
                vel.Mul(this.BulletSpeed * dt);
                bulletRef.PrevPos.Set(bulletRef.CurPos);
                bulletRef.CurPos.Add(vel);

                //Check intersection with scene objects

                //MathIntersectionSphereAABB(bulletRef.CurPos, 0.1, scene.LampLeftAABB.Position, extent)
                {
                    const intersectionPos = GStack.TempVec3_5;
                    const extent = GStack.TempVec3;
                    extent.Set(scene.LampLeftAABB.Extent);
                    extent.Mul(0.3);
                    if (!scene.bLeftLampDestroyed && bulletRef.CurPos.z > -1.0) {
                        if (
                            MathIntersectionRayAABB(
                                bulletRef.SpawnPos,
                                bulletRef.CurDir,
                                scene.LampLeftAABB.Position,
                                extent,
                                intersectionPos,
                            )
                        ) {
                            buf.LeftLampScale.x = 0.0;
                            scene.MeshLampLeftArr[3].bCanDraw = false;
                            scene.MeshLampLeftArr[2].bCanDraw = false;

                            this.bLampIntersectionThisFrame = true;

                            scene.MessageMesh.Material.bisEmmiter = true;

                            scene.bLeftLampDestroyed = true;

                            SceneBase.UniformBuffer.Params.IntersectionPosWS.Set(scene.LampLeftAABB.Position);
                            //SceneBase.UniformBuffer.Params.IntersectionPosWS.Set(this.IntersectionPos);

                            scene.ImpactComponent.OnHit(SceneBase.UniformBuffer.Params.IntersectionPosWS);
                        }
                    }

                    extent.Set(scene.LampRightAABB.Extent);
                    extent.Mul(0.3);
                    if (!scene.bRightLampDestroyed && bulletRef.CurPos.z > -1.0) {
                        if (
                            MathIntersectionRayAABB(
                                bulletRef.SpawnPos,
                                bulletRef.CurDir,
                                scene.LampRightAABB.Position,
                                extent,
                                intersectionPos,
                            )
                        ) {
                            buf.LampFlickerScale.x = 0.0;
                            scene.MeshLampRightArr[3].bCanDraw = false;
                            scene.MeshLampRightArr[2].bCanDraw = false;

                            this.bLampIntersectionThisFrame = true;

                            scene.HeartMesh[0].Material.bisEmmiter = true;
                            scene.HeartMesh[1].Material.bisEmmiter = true;
                            scene.PaintState = EPaintingState.HeartCanShoot;
                            if (audioContext) {
                                //scene.SoundBellHit.Play(audioContext);
                                scene.StartSynthSound();
                            }
                            PayneSceneDestrPainting.setAlanSubVisibility(false);

                            scene.bRightLampDestroyed = true;

                            SceneBase.UniformBuffer.Params.IntersectionPosWS.Set(scene.LampRightAABB.Position);
                            //SceneBase.UniformBuffer.Params.IntersectionPosWS.Set(this.IntersectionPos);

                            scene.ImpactComponent.OnHit(SceneBase.UniformBuffer.Params.IntersectionPosWS);
                        }
                    }

                    if (this.bLampIntersectionThisFrame) {
                        GCameraShakeController.ShakeCameraFast(0.1 * 7.5);
                    }
                }

                if (!this.bLampIntersectionThisFrame) {
                    if (bulletRef.CurPos.z > -0.01) {
                        let objIntersected = false;
                        const intersectionPos = GStack.TempVec3;
                        if (
                            MathIntersectionRayAABB(
                                bulletRef.SpawnPos,
                                bulletRef.CurDir,
                                scene.PaintingAABB.Position,
                                scene.PaintingFrameAABB.Extent,
                                intersectionPos,
                            )
                        ) {
                            this.bSceneObjectIntersectionThisFrame = true;
                            objIntersected = true;
                            bulletRef.bAlive = false;
                            this.IntersectionPos.Set(bulletRef.CurPos);
                            SceneBase.UniformBuffer.Params.IntersectionPosWS.Set(this.IntersectionPos);

                            if (scene.PaintState == 0) {
                                GCameraShakeController.ShakeCameraFast(0.1 * 1.0);
                            } else {
                                GCameraShakeController.ShakeCameraFast(0.1 * 2.0);
                            }

                            break;
                        }

                        if (!objIntersected) {
                            //intersection

                            const planePos = GStack.TempVec3_3;
                            planePos.Set3(0.0, 0.0, 0.0);
                            const planeNormal = GStack.TempVec3_4;
                            planeNormal.Set3(0.0, 0.0, 1.0);
                            MathIntersectionRayPlane(
                                bulletRef.SpawnPos,
                                bulletRef.CurDir,
                                planePos,
                                planeNormal,
                                intersectionPos,
                            );

                            bulletRef.bAlive = false;
                            this.bIntersectionThisFrame = true;
                            this.IntersectionPos.Set(intersectionPos);
                            SceneBase.UniformBuffer.Params.IntersectionPosWS.Set(this.IntersectionPos);

                            //Check for intersection with heart
                            //if (scene.PaintState >= EPaintingState.HealProtect)
                            if (scene.PaintState != EPaintingState.AfterDeath) {
                                if (
                                    MathIntersectionRayAABB(
                                        bulletRef.SpawnPos,
                                        bulletRef.CurDir,
                                        scene.HeartCenterPos,
                                        scene.HeartExtent,
                                    )
                                ) {
                                    this.NumTimesHeartAreaShot += 1;
                                    const bHeartVisible = this.NumTimesHeartAreaShot > 60;
                                    const bCanShootHeart =
                                        scene.PaintState == EPaintingState.StartDeath ||
                                        scene.PaintState == EPaintingState.Dead;

                                    if (bCanShootHeart && bHeartVisible) {
                                        this.bIntersectionThisFrame = false;
                                        this.bHeartIntersectionThisFrame = true;
                                        this.bSceneObjectIntersectionThisFrame = true;
                                        this.HeartIntersectionPos.Set(this.IntersectionPos);
                                        this.IntersectionPos.Set(scene.PaintingAABB.Position);
                                        {
                                            let jitterScale = 1.5;
                                            if (this.BulletSpawnCounter == 4) {
                                                jitterScale = 2.5;
                                            }
                                            this.IntersectionPos.x += jitterScale * (-0.5 + Math.random());
                                            this.IntersectionPos.y += jitterScale * (-0.5 + Math.random());
                                        }
                                        SceneBase.UniformBuffer.Params.IntersectionPosWS.Set(this.IntersectionPos);

                                        GCameraShakeController.ShakeCameraFast(0.1 * 4.0);
                                    } else {
                                        if (bHeartVisible) {
                                            if (scene.PaintState == EPaintingState.HealProtect) {
                                                scene.PaintState = EPaintingState.HeartVisible;
                                                PayneSceneDestrPainting.setAlanSubNum(1);
                                            }
                                            if (scene.PaintState == EPaintingState.HeartCanShoot) {
                                                scene.PaintState = EPaintingState.StartDeath;
                                                GCameraShakeController.ShakeCameraFast(0.1 * 7.5);
                                                if (audioContext) {
                                                    scene.SoundBellHit.Play(audioContext);
                                                    scene.SoundWitchScream.Play(audioContext);
                                                }
                                                scene.SoundEnableBackFilter();
                                                //scene.NumTimesMainThemePlayed = 2;
                                            }
                                        }
                                    }
                                }
                            }

                            // GCameraShakeController.ShakeCameraFast(0.1 * 0.25);
                            break;
                        }
                    }
                }
            }
        }

        this.TimeSinceLastSpawn += GTime.Delta * GTime.DeltaTimeScale;
        this.TimeSinceLastSpawn2 += GTime.Delta * GTime.DeltaTimeScale;
    }

    Render(gl: WebGL2RenderingContext, scene: PayneSceneDestrPainting) {
        const color = GStack.TempVec3;
        color.SetSame(0.4);

        for (const bulletRef of this.BulletsArr) {
            if (bulletRef.bAlive && bulletRef.CurPos.z > -8.0) {
                const bd = bulletRef.CurPos.z;
                const dThres = -2.0;
                if (bd < dThres) {
                    const ct = MathMapToRangeClamped(bd, -10.0, dThres, 0.0, 1.0);
                    color.SetSame(0.45 * ct);
                } else {
                    color.SetSame(0.45);
                }
                GSimpleShapesRenderer.GInstance!.RenderPoint(gl, bulletRef.CurPos, 0.015 * 0.7, color, true);
            }
        }

        //Trail
        {
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
            gl.blendEquation(gl.FUNC_ADD);

            for (const bulletRef of this.BulletsArr) {
                if (bulletRef.bAlive && bulletRef.CurPos.z > -8.0) {
                    const bd = bulletRef.CurPos.z;
                    const dThres = -1.5;
                    let alpha = 1.0;
                    // eslint-disable-next-line prefer-const
                    let maxRayLength = 2.5;
                    const colBr = 0.75;
                    if (GBulletSystem.GInstance!.BulletTimeState > 0) {
                        maxRayLength = 6.0;
                        //colBr = 1.0;
                    }
                    if (bd < dThres) {
                        // eslint-disable-next-line prefer-const
                        let ct = MathMapToRangeClamped(bd, -10.0, dThres, 0.0, 1.0);
                        //ct *= ct;
                        color.SetSame(0.35 * colBr * ct);
                        alpha *= ct;
                    } else {
                        color.SetSame(0.35 * colBr);
                    }

                    const curStart = GStack.TempVec3_2;
                    curStart.Set(bulletRef.SpawnPos);
                    curStart.Negate(bulletRef.CurPos);
                    let curRayL = MathGetVec3Length(curStart);
                    curRayL = Math.min(maxRayLength, curRayL);
                    curStart.Normalize();
                    curStart.Mul(curRayL);
                    curStart.Add(bulletRef.CurPos);

                    GSimpleShapesRenderer.GInstance!.RenderLineAlpha(
                        gl,
                        curStart,
                        bulletRef.CurPos,
                        0.005 * 1.5,
                        color,
                        alpha,
                    );

                    if (bulletRef.CurPos.z > -5.0) {
                        alpha = 1.0;
                        const brScle = 1.0;
                        color.Set3(1.0 * brScle, 0.3 * brScle, 0.0);
                        GSimpleShapesRenderer.GInstance!.RenderLineAlpha(
                            gl,
                            curStart,
                            bulletRef.CurPos,
                            0.0015,
                            color,
                            1.0,
                        );
                    }
                }
            }

            gl.disable(gl.BLEND);
        }

        if (this.TimeSinceLastSpawn2 < this.SpawnFrequencyDelta) {
            gl.enable(gl.BLEND);
            gl.blendEquation(gl.FUNC_ADD);
            gl.blendFunc(gl.ONE, gl.ONE);

            let t = this.TimeSinceLastSpawn2 / this.SpawnFrequencyDelta;
            t *= t;

            const colorCur = GStack.TempVec3;
            const cb = MathLerp(0.125, 0.0, t);
            colorCur.Set3(1.0 * cb, 0.5 * cb, 0.3 * cb);

            const yOffset = 0.2;
            const drawPosCur = GStack.TempVec3_4;
            this.GetBulletSpawnPos(drawPosCur, true);
            drawPosCur.y -= yOffset;

            const scale = (0.5 + t * 0.5) * 0.95;

            GSimpleShapesRenderer.GInstance!.RenderPoint(
                gl,
                drawPosCur,
                scale,
                colorCur,
                false,
                1.1,
                scene.ImpactComponent.LightFlareTexture3,
            );

            this.GetBulletSpawnPos(drawPosCur, false);
            drawPosCur.y -= yOffset;
            GSimpleShapesRenderer.GInstance!.RenderPoint(
                gl,
                drawPosCur,
                scale,
                colorCur,
                false,
                1.1,
                scene.ImpactComponent.LightFlareTexture3,
            );

            gl.disable(gl.BLEND);
        }
    }
}
